import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ButtonWithAction,
  ButtonWithLink,
  SidebarSingleContent,
  Box,
  Cluster,
  constants,
  Center,
  DisplayBox,
  Text,
  Title,
  Spinner,
} from '@thecb/components';
import { transformReplaceFormData } from '../../utils/helpers';
import AlertBar from '../../components/alert-bar';
import { DynamicLookupFieldGroup } from '../../components/form-blocks';
import CartSidebar from '../../components/cart-sidebar';
import { useDispatch, useSelector } from 'react-redux';
import SharedAlertBar from '../../components/alert-bar/SharedAlertBar';
import { addToCartRequest } from './Lookup.state';
const Header = ({ alertBar, alertBarActions }) => {
  const { alerts: sharedAlerts } = useSelector((state) => state.shared);
  return (
    <Box width="100%" padding="16px 0">
      <Center maxWidth="85.714rem">
        {alertBar.alerts.length > 0 && (
          <Box padding="16px 0">
            <AlertBar {...alertBar} {...alertBarActions} />
          </Box>
        )}
        {sharedAlerts.length > 0 && (
          <Box padding="16px 0">
            <SharedAlertBar />
          </Box>
        )}
        <Title
          color={constants.colors.CHARADE_GREY}
          weight={constants.fontWeights.FONT_WEIGHT_BOLD}
        >
          Add
        </Title>
      </Center>
    </Box>
  );
};

const Lookup = ({ fetchLookupById, alertBars, actions }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading: isLoadingLookup } = useSelector((state) => state.lookup);
  const {
    error: hasErrors,
    currentProduct: {
      status,
      fetching: isLoadingProduct,
      form_data,
      product_data,
      transforms,
      required_fields,
    },
  } = useSelector((state) => state.products);
  const canRenderForm =
    !isLoadingProduct &&
    !hasErrors &&
    status === 'success' &&
    !!form_data &&
    !!product_data;
  const initialFormData = useSelector((state) => state.formData);
  const [formData, setFormData] = useState({ inputValue: initialFormData });

  useEffect(() => {
    fetchLookupById(id);
  }, []);

  const onChange = (args, changedField) => {
    if (!!transforms && transforms?.hasOwnProperty('replace')) {
      const newFormData = transformReplaceFormData(
        args.formData,
        changedField,
        transforms,
      );
      setFormData(newFormData);
    } else {
      setFormData(args.formData);
    }
  };
  const handleSubmit = (formData) => {
    if (isLoadingLookup || !isFormAmountValid) {
      return;
    }

    const { amount, quantity, ...externalAttributes } = formData;

    dispatch(
      addToCartRequest({
        receivables: [
          {
            recorded_price: amount,
            item_id: id,
            artifact_items: {},
            validated: product_data.validated,
            accepts_underpayment: product_data.accepts_underpayment,
            accepts_overpayment: product_data.accepts_overpayment,
            receivable_category: product_data.receivable_category,
            external_attributes: externalAttributes,
            quantity,
          },
        ],
        navigate,
        location,
      }),
    );
  };

  const isFormAmountValid = useMemo(() => {
    const amount = parseFloat(formData.amount);
    const isDiscountAllowed = form_data?.amount?.discount_allowed === true;

    if (isNaN(amount)) {
      return false;
    }

    return amount > 0 || (isDiscountAllowed && amount !== 0);
  }, [formData.amount, form_data?.amount?.discount_allowed]);

  const formActions = (
    <Box padding="1rem 0 0 0">
      <Cluster justify="flex-end" align="center" childGap="1rem">
        <ButtonWithLink
          text="Cancel"
          type="button"
          url="/pos/products"
          isLoading={false}
          variant="secondary"
          dataQa="Cancel"
          textExtraStyles="font-size: 1rem;"
        />
        <ButtonWithAction
          type="submit"
          text="Submit"
          variant="primary"
          dataQa="Submit"
          disabled={isLoadingLookup || !isFormAmountValid}
          isLoading={isLoadingLookup}
          textExtraStyles="font-size: 1rem;"
        />
      </Cluster>
    </Box>
  );

  /* TODO
   ** abstract to a <Breadcrumbs /> component
   **/
  const breadcrumbs = (
    <Box padding="0 0 1rem 0">
      <Cluster
        direction="row"
        justify="flex-start"
        align="center"
        childGap="1rem"
      >
        <ButtonWithLink
          extraStyles={'min-width: 0; margin: 0;'}
          url={'/pos/products'}
          text={product_data?.receivable_category}
          variant="smallGhost"
          isLoading={false}
          textExtraStyles="font-size: 1rem;"
        />
        {'>'}
        <Text>{product_data?.name}</Text>
      </Cluster>
    </Box>
  );

  const lookupAttributes = form_data;
  const content = (
    <DisplayBox padding="1rem 0">
      {isLoadingProduct && <Spinner size="100" />}
      {canRenderForm && (
        <>
          {breadcrumbs}
          <DynamicLookupFieldGroup
            attributes={lookupAttributes}
            attributesGroupName={product_data.name}
            requiredFields={required_fields}
            submitForm={handleSubmit}
            onChange={onChange}
            formActions={formActions}
            formData={formData}
            setFormData={setFormData}
            overrides={transforms}
          />
        </>
      )}
    </DisplayBox>
  );

  return (
    <SidebarSingleContent
      header={
        <Header
          key="LookupHeader"
          alertBar={alertBars.lookupAlertBars}
          alertBarActions={actions.alertBars.lookupAlertBars}
        />
      }
      mainContent={content}
      sidebarContent={<CartSidebar />}
      sidebarTargetWidth="27.429rem"
      sidebarWrapperMaxWidth="85.714rem"
      sidebarOnRight={true}
      childGap="0.5rem"
    />
  );
};

export default Lookup;
